import { useState } from "react";
import T from "../theme";
import { GS } from "../components/ui/GlobalStyles";
import { Icon } from "../components/ui/Icon";
import { Card } from "../components/ui/Card";
import { Btn } from "../components/ui/Btn";
import { Field, inp } from "../components/ui/Field";
import { changePassword } from "../lib/db";
import logoEscuro from "../assets/logo_escuro.svg";
import logoBranco from "../assets/logo_branco.svg";

const PW_MIN = 8;
const PW_RE_UPPER = /[A-Z]/;
const PW_RE_LOWER = /[a-z]/;
const PW_RE_DIGIT = /\d/;

const LogoHeader = ({ dark }) => (
  <div style={{ textAlign: "center", marginBottom: 28 }}>
    <img src={dark ? logoEscuro : logoBranco} alt="VillaGest" style={{ height: 38, width: "auto", display: "block", margin: "0 auto 10px" }} />
    <p style={{ color: T.textMuted, fontSize: 14 }}>Gestão de Condomínio</p>
  </div>
);

const Check = ({ ok, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: ok ? T.success : T.textMuted, marginBottom: 4 }}>
    <Icon name={ok ? "check" : "x"} size={13} color={ok ? T.success : T.textMuted} />
    {label}
  </div>
);

export const ForceChangePassword = ({ dark, onToggleDark, onPasswordChanged, showToast }) => {
  const [pw, setPw]             = useState("");
  const [pw2, setPw2]           = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [showPw2, setShowPw2]   = useState(false);
  const [err, setErr]           = useState("");
  const [loading, setLoading]   = useState(false);

  const hasLen   = pw.length >= PW_MIN;
  const hasUpper = PW_RE_UPPER.test(pw);
  const hasLower = PW_RE_LOWER.test(pw);
  const hasDigit = PW_RE_DIGIT.test(pw);
  const match    = pw && pw2 && pw === pw2;
  const allValid = hasLen && hasUpper && hasLower && hasDigit && match;

  const handleSubmit = async () => {
    if (!allValid) {
      setErr("Preencha todos os requisitos da password.");
      return;
    }

    setLoading(true);
    setErr("");
    try {
      await changePassword(pw);
      showToast("Password alterada com sucesso!", "success");
      onPasswordChanged();
    } catch (ex) {
      setErr(ex.message || "Erro ao alterar password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, position: "relative" }}>
      <GS />
      {/* Dark toggle */}
      <button
        onClick={onToggleDark}
        title={dark ? "Modo Claro" : "Modo Escuro"}
        style={{ position: "absolute", top: 18, right: 20, display: "flex", alignItems: "center", gap: 6, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: T.textMuted, fontSize: 13, fontWeight: 500, boxShadow: T.shadow }}>
        <Icon name={dark ? "sun" : "moon"} size={14} color={T.textMuted} />
        {dark ? "Modo Claro" : "Modo Escuro"}
      </button>

      <div style={{ width: "100%", maxWidth: 420, animation: "fadeIn 0.4s ease" }}>
        <LogoHeader dark={dark} />

        <Card style={{ padding: "28px 26px" }}>
          {/* Header icon */}
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: T.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <Icon name="lock" size={24} color={T.primary} />
            </div>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: T.text, marginBottom: 4 }}>Definir nova password</h2>
            <p style={{ color: T.textMuted, fontSize: 13, lineHeight: 1.5 }}>
              Por razões de segurança, defina uma nova password para a sua conta.
            </p>
          </div>

          {/* New password */}
          <Field label="Nova password">
            <div style={{ position: "relative" }}>
              <input
                value={pw}
                onChange={e => { setPw(e.target.value); setErr(""); }}
                type={showPw ? "text" : "password"}
                placeholder="••••••••"
                style={{ ...inp(err), paddingRight: 40 }}
                onFocus={e => e.target.style.border = `1px solid ${T.primary}`}
                onBlur={e => e.target.style.border = `1px solid ${err ? T.danger : T.border}`}
              />
              <button
                onClick={() => setShowPw(s => !s)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.textMuted, display: "flex" }}>
                <Icon name={showPw ? "eyeOff" : "eye"} size={16} />
              </button>
            </div>
          </Field>

          {/* Password requirements */}
          <div style={{ marginBottom: 14, marginTop: -4 }}>
            <Check ok={hasLen} label={`Mínimo ${PW_MIN} caracteres`} />
            <Check ok={hasUpper} label="Uma letra maiúscula" />
            <Check ok={hasLower} label="Uma letra minúscula" />
            <Check ok={hasDigit} label="Um número" />
          </div>

          {/* Confirm password */}
          <Field label="Confirmar password">
            <div style={{ position: "relative" }}>
              <input
                value={pw2}
                onChange={e => { setPw2(e.target.value); setErr(""); }}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
                type={showPw2 ? "text" : "password"}
                placeholder="••••••••"
                style={{ ...inp(pw2 && !match ? "err" : ""), paddingRight: 40 }}
                onFocus={e => e.target.style.border = `1px solid ${T.primary}`}
                onBlur={e => e.target.style.border = `1px solid ${pw2 && !match ? T.danger : T.border}`}
              />
              <button
                onClick={() => setShowPw2(s => !s)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.textMuted, display: "flex" }}>
                <Icon name={showPw2 ? "eyeOff" : "eye"} size={16} />
              </button>
            </div>
          </Field>

          {pw2 && !match && (
            <p style={{ color: T.danger, fontSize: 12, marginTop: -8, marginBottom: 12 }}>As passwords não coincidem.</p>
          )}

          {err && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 12px", background: T.dangerLight, border: `1px solid ${T.dangerBorder}`, borderRadius: T.radius, color: T.dangerText, fontSize: 13, marginBottom: 14 }}>
              <Icon name="alert" size={14} color={T.dangerText} />{err}
            </div>
          )}

          <Btn onClick={handleSubmit} disabled={loading || !allValid} style={{ width: "100%", justifyContent: "center", padding: "11px", fontSize: 15 }}>
            {loading ? "A guardar..." : "Definir password e entrar"}
          </Btn>
        </Card>
      </div>
    </div>
  );
};
